import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import type { Column, RowAction } from 'design-system-web';

import {
  type TemplateLibraryTableRow,
  getTemplateLibraryColumns,
  getTemplateLibraryRowActions,
} from '../pages/templateLibraryTableConfig';

type UseTemplateLibraryTableArgs = {
  /** Whether the hide/unhide feature is enabled for this channel. */
  hideTemplateEnabled: boolean;
  /** Row actions are only offered on child channels. */
  isChildChannel: boolean;
  onCustomize: (row: TemplateLibraryTableRow) => void;
  onToggleVisibility: (row: TemplateLibraryTableRow) => void;
};

type UseTemplateLibraryTableResult = {
  columns: Column<TemplateLibraryTableRow>[];
  /**
   * A per-column-set localStorage bucket for the Table's persisted column
   * order. It also doubles as the Table's `key` to force a remount when the
   * hide-template flag flips — see the Table props in the page for the full
   * rationale.
   */
  tableKey: string;
  rowActions: RowAction<TemplateLibraryTableRow>[] | undefined;
  filterTemplatesByName: (
    row: TemplateLibraryTableRow,
    keyword: string
  ) => boolean;
};

/**
 * Builds the Template Library table's view configuration — columns, the
 * persisted-order/remount key, keyword filter, and (child-channel only) row
 * actions. The actual customize/toggle handlers are supplied by the caller
 * since they depend on the caller's resolved template map.
 */
export function useTemplateLibraryTable({
  hideTemplateEnabled,
  isChildChannel,
  onCustomize,
  onToggleVisibility,
}: UseTemplateLibraryTableArgs): UseTemplateLibraryTableResult {
  const { t } = useTranslation();

  const columns = useMemo(
    () =>
      getTemplateLibraryColumns(t, {
        includeVisibilityColumn: hideTemplateEnabled,
      }),
    [t, hideTemplateEnabled]
  );

  // Isolated per column set — see the comment on the Table's `key`/`tableKey`
  // props below for why the visibility column needs its own persisted-order
  // cache bucket rather than sharing one with the base (no-visibility) table.
  const tableKey = `inspections-template-library-v2${
    hideTemplateEnabled ? '-visibility' : ''
  }`;

  const filterTemplatesByName = useCallback(
    (row: TemplateLibraryTableRow, keyword: string) => {
      const q = keyword.trim().toLowerCase();

      if (!q) {
        return true;
      }

      return row.name.toLowerCase().includes(q);
    },
    []
  );

  const rowActions = useMemo(
    () =>
      isChildChannel
        ? getTemplateLibraryRowActions(t, {
            onCustomize,
            onToggleVisibility,
            includeVisibilityActions: hideTemplateEnabled,
          })
        : undefined,
    [isChildChannel, t, onCustomize, onToggleVisibility, hideTemplateEnabled]
  );

  return { columns, tableKey, rowActions, filterTemplatesByName };
}
